/**
 * @file AuthContext.tsx
 * @description Authentication context with Google OAuth and whitelist
 * @author XiaVan Team
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import {
  User,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth'
import {
  doc,
  getDoc,
  setDoc,
  Timestamp,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore'
import { auth, googleProvider, db } from '@/lib/firebase'
import { getAccessRequestStatus } from '@/lib/api-access-requests'
import { logger } from '@/lib/logger'

/**
 * Authentication state
 */
interface AuthContextType {
  user: User | null
  loading: boolean
  isAuthorized: boolean
  accessRequestStatus: 'none' | 'pending' | 'approved' | 'denied'
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  error: string | null
  refreshAccessRequestStatus: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * Authentication provider
 * Manages login state with Google OAuth
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isAuthorized, setIsAuthorized] = useState(false)
  const [accessRequestStatus, setAccessRequestStatus] = useState<'none' | 'pending' | 'approved' | 'denied'>('none')

  /**
   * Check if user email is in allowedEmails Firestore collection
   * If not authorized, also check access request status
   */
  const checkAuthorization = async (authUser: User | null) => {
    console.log('🔍 [checkAuthorization] Called with user:', authUser?.email)
    
    if (!authUser || !authUser.email) {
      console.log('⚠️ [checkAuthorization] No user or email, setting isAuthorized = false')
      setIsAuthorized(false)
      setAccessRequestStatus('none')
      return
    }

    try {
      console.log('🔍 [checkAuthorization] Querying Firestore allowedEmails for:', authUser.email)
      const allowedEmailRef = collection(db, 'allowedEmails')
      const q = query(allowedEmailRef, where('email', '==', authUser.email))
      const allowedEmailSnapshot = await getDocs(q)
      
      console.log('📊 [checkAuthorization] Query result - empty:', allowedEmailSnapshot.empty, 'size:', allowedEmailSnapshot.size)
      
      const authorized = !allowedEmailSnapshot.empty
      console.log('🎯 [checkAuthorization] Setting isAuthorized =', authorized)
      setIsAuthorized(authorized)
      
      if (authorized) {
        console.log('✅ User authorized:', authUser.email)
        setAccessRequestStatus('none') // Reset status if authorized
      } else {
        console.warn('⚠️ User NOT authorized:', authUser.email)
        
        // Check access request status
        console.log('🔍 [checkAuthorization] Checking access request status...')
        const requestStatus = await getAccessRequestStatus(authUser.email)
        console.log('📊 [checkAuthorization] Access request status:', requestStatus)
        setAccessRequestStatus(requestStatus)
      }
    } catch (err) {
      console.error('❌ Error checking authorization:', err)
      setIsAuthorized(false)
      setAccessRequestStatus('none')
    }
  }

  /**
   * Manually refresh access request status
   * Used after user submits new access request
   */
  const refreshAccessRequestStatus = async () => {
    if (user && user.email && !isAuthorized) {
      console.log('🔄 [refreshAccessRequestStatus] Refreshing for:', user.email)
      const status = await getAccessRequestStatus(user.email)
      setAccessRequestStatus(status)
      console.log('✅ [refreshAccessRequestStatus] Updated to:', status)
    }
  }

  /**
   * Login with Google OAuth using popup
   */
  const signInWithGoogle = async () => {
    try {
      setError(null)
      console.log('🔐 [signInWithGoogle] Opening Google popup...')
      
      // Use popup instead of redirect
      const result = await signInWithPopup(auth, googleProvider)
      console.log('✅ [signInWithGoogle] Popup completed for:', result.user.email)
      
      // Check if email is authorized
      const isAuthorized = await createOrUpdateUserDocument(result.user)
      console.log('✅ [signInWithGoogle] Authorization check:', isAuthorized)
      
      if (!isAuthorized) {
        // Email NOT in allowedEmails → keep user signed in but show unauthorized state
        // This allows LoginPage to show "Request Access" with pre-filled user info
        console.log('⚠️ [signInWithGoogle] Email not authorized:', result.user.email)
        console.log('📌 [signInWithGoogle] User remains signed in for access request')
        
        // Check access request status
        if (result.user.email) {
          const requestStatus = await getAccessRequestStatus(result.user.email)
          setAccessRequestStatus(requestStatus)
        }
        return
      }
      
      console.log('✅ [signInWithGoogle] Login successful!')
    } catch (err: any) {
      // Silently ignore user-cancelled popups (not real errors)
      if (err.code === 'auth/cancelled-popup-request' || err.code === 'auth/popup-closed-by-user') {
        console.log('ℹ️ [signInWithGoogle] User cancelled popup, no error shown')
        return
      }
      
      // Handle popup blocked error specifically
      if (err.code === 'auth/popup-blocked') {
        setError('⚠️ El popup fue bloqueado. Por favor, permite popups para este sitio y vuelve a intentarlo.')
      } else {
        const message = err instanceof Error ? err.message : 'Error signing in with Google'
        console.error('❌ Login error:', err)
        setError(message)
      }
    }
  }

  /**
   * Create or update user document in Firestore
   * Assigns role from allowedEmails collection and syncs audit trail
   * @returns {Promise<boolean>} true if user is authorized, false otherwise
   */
  const createOrUpdateUserDocument = async (authUser: User): Promise<boolean> => {
    try {
      const userRef = doc(db, 'users', authUser.uid)
      const userDoc = await getDoc(userRef)

      // Get role AND audit dates from allowedEmails collection
      const allowedEmailRef = doc(db, 'allowedEmails', authUser.email!)
      const allowedEmailDoc = await getDoc(allowedEmailRef)

      // Check if user is in allowedEmails - CRITICAL for authorization
      if (!allowedEmailDoc.exists()) {
        console.warn('⚠️ User email not in allowedEmails:', authUser.email)
        return false // User NOT authorized
      }

      const allowedData = allowedEmailDoc.data()
      const role = allowedData.role
      const requestedAt = allowedData.requestedAt || null    // Audit: When user requested
      const approvedAt = allowedData.approvedAt || null      // Audit: When admin approved
      const approvedBy = allowedData.addedBy || null         // Audit: Which admin approved

      if (!userDoc.exists()) {
        // Create new user document WITH audit trail
        await setDoc(userRef, {
          email: authUser.email!,
          displayName: authUser.displayName || authUser.email!.split('@')[0],
          photoURL: authUser.photoURL,
          role: role,
          requestedAt: requestedAt,    // Sync from allowedEmails
          approvedAt: approvedAt,      // Sync from allowedEmails
          approvedBy: approvedBy,      // Sync from allowedEmails
          createdAt: Timestamp.now(),
          lastLogin: Timestamp.now(),
        })
        console.log('✅ User document created:', authUser.uid)
      } else {
        // Update last login, sync role AND audit dates
        const existingData = userDoc.data()
        await setDoc(
          userRef,
          {
            lastLogin: Timestamp.now(),
            role: role, // Sync role from allowedEmails
            requestedAt: requestedAt || existingData.requestedAt || null,    // Preserve or sync
            approvedAt: approvedAt || existingData.approvedAt || null,        // Preserve or sync
            approvedBy: approvedBy || existingData.approvedBy || null,        // Preserve or sync
            displayName: authUser.displayName || existingData.displayName,
            photoURL: authUser.photoURL || existingData.photoURL,
          },
          { merge: true }
        )
        console.log('✅ User document updated:', authUser.uid)
      }

      return true // User IS authorized
    } catch (err) {
      console.error('❌ Error creating/updating user document:', err)
      return false
    }
  }

  /**
   * Sign out
   */
  const signOut = async () => {
    try {
      setError(null)
      await firebaseSignOut(auth)
      console.log('👋 Session closed')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error signing out'
      console.error('❌ Sign out error:', err)
      setError(message)
    }
  }

  /**
   * Listen to authentication state changes
   */
  useEffect(() => {
    console.log('🔄 [useEffect] Auth listener initialized')

    // Listen to auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (currentUser: User | null) => {
      console.log('🔄 [onAuthStateChanged] User changed:', currentUser?.email || 'null')
      setUser(currentUser)
      
      // Initialize logger with current user (enables conditional logging)
      if (currentUser) {
        // Convert Firebase User to app User type
        const appUser = {
          id: currentUser.uid,
          email: currentUser.email!,
          displayName: currentUser.displayName || '',
          photoURL: currentUser.photoURL || undefined,
          role: 'user' as const, // Will be updated after authorization check
          createdAt: Timestamp.now(),
          lastLogin: Timestamp.now(),
        }
        logger.init(appUser)
      } else {
        logger.init(null)
      }
      
      setLoading(false)
      console.log('⏳ [onAuthStateChanged] Loading set to false')

      // Check authorization from Firestore allowedEmails
      await checkAuthorization(currentUser)
    })

    return () => {
      console.log('🔚 [useEffect] Cleaning up auth listener')
      unsubscribe()
    }
  }, [])

  const value = {
    user,
    loading,
    isAuthorized,
    accessRequestStatus,
    signInWithGoogle,
    signOut,
    error,
    refreshAccessRequestStatus,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Hook to use authentication
 * @example
 * const { user, isAuthorized, signInWithGoogle, signOut } = useAuth()
 */
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
